/**
 * Medical PA order three-dots menu wiring (options, handlers, nested modals).
 *
 * @module MedicalPaThreeDotsMenu
 */
import { Item } from "../../../data-model/documentModalWithSideBav";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { UseHealthCareFacilityData } from "../../../hooks/useHealthCareFacility";
import ThreeDotsMenu from "../ThreeDotsMenu";
import ThreeDotsModals from "../ThreeDotsModals";
import { useMenuActions, useMenuOptions, usePmgIngestionExists } from "./hooks";

export interface MedicalPaThreeDotsMenuProps {
  /** NCCN guideline items for the order */
  items: Item[];
  /** Health care facility data */
  healthCareFacilityData: UseHealthCareFacilityData;
  /** Auth on file data */
  authOnFile: AuthOnFile;
  /** General order details */
  generalOrderDetails: GeneralOrderDetails;
  /** Submission data */
  submissionData: Submission;
  /** BigQuery screen name used for analytics events emitted from this menu. */
  screenName: ScreenNamesForBigQuery;
}

/**
 * A specialized three-dots menu for Medical PA orders.
 *
 * This component wraps the generic ThreeDotsMenu with Medical PA specific:
 * - Options (View Guidelines, View Tx Plan, View in Onco EMR, View DX Codes, View NAR Grid)
 * - Action handlers (with event tracking)
 * - Modals (Doc Viewer, Image Viewer, NAR Grid)
 *
 * @example
 * ```tsx
 * <MedicalPaThreeDotsMenu
 *   items={guidelinesDocuments ?? []}
 *   healthCareFacilityData={healthCareFacilityData}
 *   authOnFile={authOnFile}
 *   generalOrderDetails={generalOrderDetails}
 * />
 * ```
 */
const MedicalPaThreeDotsMenu: React.FC<MedicalPaThreeDotsMenuProps> = ({
  items,
  healthCareFacilityData,
  authOnFile,
  generalOrderDetails,
  submissionData,
  screenName,
}) => {
  const { status: pmgIngestionStatus, refetch: refetchPmgIngestionExists } =
    usePmgIngestionExists(generalOrderDetails);
  const {
    executeAction,
    handleMouseEnter,
    abortControllerRef,
    openedModalId,
    isIngesting,
  } = useMenuActions({
    items,
    healthCareFacilityData,
    authOnFile,
    generalOrderDetails,
    submissionData,
    screenName,
    onIngestComplete: refetchPmgIngestionExists,
  });
  const { options } = useMenuOptions(
    items,
    pmgIngestionStatus?.exists ?? false,
    isIngesting,
  );

  return (
    <>
      <ThreeDotsMenu
        options={options}
        onOptionClick={executeAction}
        onMenuOpen={handleMouseEnter}
        id="medical-pa-form"
      />
      <ThreeDotsModals
        openedModalId={openedModalId}
        authOnFile={authOnFile}
        abortControllerRef={abortControllerRef}
        screenName={screenName}
      />
    </>
  );
};

export default MedicalPaThreeDotsMenu;
